<template>
  <ValidationProvider
    ref="select"
    v-slot="{ errors, required, ariaInput, ariaMsg, reset }"
    class="gl-select__wrapper"
    :class="{
      'gl-select__wrapper--full': fullWidth,
      'gl-select__light-gray': lightGray,
      'gl-select__v2': v2,
    }"
    :name="name || label"
    :rules="rules"
    tag="div"
    :vid="vid"
  >
    <div
      v-if="label || maxLength"
      class="flex space-between"
    >
      <label
        class="gl-select__label"
        :class="{ 'gl-select__label--invalid': errors[0] }"
        :for="name"
      >
        {{ label || name }}
      </label>
      <div
        v-if="rules.includes('max') && maxLength"
        class="gl-select__label gl-modal__title--capitalize"
      >
        {{ value.length }} / {{ maxLength }} Symbols
      </div>
    </div>
    <v-select
      v-model="innerValue"
      :clearable="clearable"
      :disabled="disabled"
      :label="labelSelect"
      :min="min"
      :multiple="multiple"
      :options="options"
      :placeholder="placeholder"
      :searchable="searchable"
      :style="`min-height: ${minHeight}${units}; margin-bottom: ${extraMarginBottom}; width: ${width};`"
      @open="open"
    >
      <template #selected-option="selected">
        <slot
          :data="selected"
          name="selected-slot"
        />
      </template>
      <template #option="option">
        <slot
          :data="option"
          name="option-slot"
        />
      </template>
      <template
        v-if="customArrowDown"
        #open-indicator="{ attributes }"
      >
        <span v-bind="attributes">
          <div class="gl-select__custom-arrow-down">
            <gl-icon
              gray
              :height="5"
              name="I-ArrowDropDown"
              :width="10"
            />
          </div>
        </span>
      </template>
      <template
        v-else
        #open-indicator="attributes"
      >
        <slot
          :attributes="attributes"
          name="open-indicator"
        />
      </template>
    </v-select>
    <span
      v-if="errors[0]"
      v-bind="ariaMsg"
      class="gl-select__error"
      :class="{ 'gl-select__error--block': isBlockErrorStyle }"
    >{{ errors[0] }}</span>
  </ValidationProvider>
</template>
<script>
// components
import GlIcon from '@/components/gl-icon'
import vSelect from 'vue-select'
// validate
import { ValidationProvider } from 'vee-validate'

export default {
  name: 'GlSelect',
  components: {
    ValidationProvider,
    vSelect,
    GlIcon,
  },
  inheritAttrs: false,
  props: {
    vid: {
      type: String,
      default: undefined,
    },
    maxLength: {
      type: String,
      default: '',
    },
    isBlockErrorStyle: {
      type: Boolean,
      default: false,
    },
    lightGray: {
      type: Boolean,
      default: false,
    },
    name: {
      type: String,
      default: '',
    },
    units: {
      type: String,
      default: 'px',
    },
    extraMarginBottom: {
      type: String,
      default: '0',
    },
    min: {
      type: String,
      default: '0',
    },
    minHeight: {
      type: [Number, String],
      default: 30,
    },
    width: {
      type: String,
      default: '100%',
    },
    placeholder: {
      type: String,
      default: '',
    },
    label: {
      type: String,
      default: '',
    },
    rules: {
      type: [Object, String],
      default: '',
    },
    fullWidth: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    clearable: {
      type: Boolean,
      default: false,
    },
    options: {
      type: Array,
      default: () => [],
    },
    value: {
      type: [Object, Array, String],
      default: () => [],
    },
    multiple: {
      type: Boolean,
      default: false,
    },
    searchable: {
      type: Boolean,
      default: false,
    },
    labelSelect: {
      type: String,
      default: '',
    },
    customArrowDown: {
      type: Boolean,
      default: false,
    },
    v2: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    innerValue: {
      get: function () {
        return this.value
      },
      set: function (e) {
        this.$emit('change', e)
        this.$emit('input', e)
      },
    },
  },
  methods: {
    clearValue() {
      this.innerValue = null
      this.$emit('clear')
    },
    open() {
      this.$emit('open')
    },
  },
}
</script>
<style>
.gl-select__wrapper input {
  font-family: 'Exo 2', 'Montserrat', sans-serif;
}

.gl-select__wrapper .vs__search {
  font-size: 16px;
}

.gl-select__light-gray .v-select {
  border-radius: 8px;
  background: var(--light-grey-f-8);
  border: none;
  box-shadow: none;
}

.gl-select__wrapper {
  display: flex;
  flex-direction: column;
  position: relative;
}
.gl-select__label {
  font-size: 14px;
  margin-bottom: 8px;
  font-weight: 500;
  color: var(--light-grey-59);
}
.gl-select__input--password-icon {
  cursor: pointer;
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
}
.gl-select__error {
  color: var(--lipstick);
  font-size: 14px;
  margin-top: 12px;
  font-weight: 500;
}
.gl-select__error--block {
  display: block;
}
.gl-select__label--invalid {
  color: var(--lipstick);
}
.gl-select__custom-arrow-down {
  display: flex;
  align-items: center;
  justify-content: center;
}
</style>

<style scoped>
/* v2 */
.gl-select__v2::v-deep .v-select {
  border-radius: 8px;
  background: var(--Basic-100, rgba(9, 21, 64, 0.04));
}
.gl-select__v2::v-deep .vs-open {
  border: 1px solid var(--Basic-900, #091540);
}

.gl-select__v2::v-deep .vs__selected {
  color: var(--Basic-900, #091540);
  font-size: var(--Size-M, 14px);
  font-weight: 500;
  line-height: var(--Line-Height-L, 20px);
  letter-spacing: var(--Letter-Spacing-S, -0.2px);
}

.gl-select__v2::v-deep .gl-select__wrapper .gl-icon--I-ArrowDropDown {
  fill: var(--Basic-900, #091540) !important;
}

.gl-select__v2::v-deep .vs__actions {
  padding: 4px 10px 0 3px;
}

.gl-select__v2::v-deep .vs__dropdown-toggle {
  border: none;
  border-radius: 8px;
  background: var(--Basic-100, rgba(9, 21, 64, 0.04));
}
.gl-select__v2::v-deep .vs--open .vs__dropdown-toggle {
  outline: 1px solid var(--Basic-900, #091540);
  border-bottom-left-radius: 6px;
  border-bottom-right-radius: 6px;
}

.gl-select__v2::v-deep .vs__dropdown-menu {
  font-size: var(--Size-M, 14px);
  top: calc(100% + 5px);
  padding: 8px 0px;
  border-radius: 3px;
  box-shadow: 0px 4px 16px 0px rgba(9, 21, 64, 0.1);
  border: none;
}
.gl-select__v2::v-deep .vs__dropdown-menu .vs__dropdown-option {
  padding: 8px 20px;
}

.gl-select__v2::v-deep ::placeholder {
  color: var(--Basic-700, rgba(9, 21, 64, 0.56));
}

.gl-select__v2::v-deep .vs__selected {
  padding: 0 8px;
}
.gl-select__v2::v-deep .vs__dropdown-option--selected {
  color: var(--Basic-900, #091540) !important;
  font-weight: 700;
}
/* v2 */
</style>
